<template>
    <div class="chat-panel">
        <div class="chat-body" ref="listRef">
            <div v-if="!messagelist.length" class="empty-state">
                <img src="@/assets/images/robot.png" alt="">
                <h2>还没有消息</h2>
                <p>发一句问候，开启这个聊天室的第一段对话。</p>
            </div>

            <div v-for="(message, idx) in messagelist" :key="message.id || idx" class="message-row"
                :class="isOutgoing(message) ? 'outgoing' : 'incoming'">
                <img class="avatar" :src="avatarSrc(message)" alt="avatar">
                <div class="bubble-wrap">
                    <div class="message-meta">
                        <span class="username">{{ message.username || '匿名用户' }}</span>
                        <span class="time">{{ formatTime(message.time || message.timestamp || message.create_time || message.createdAt || message.date || message.datetime) }}</span>
                    </div>
                    <div class="message-bubble">
                        {{ message.message || message.content }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { nextTick, ref, watch } from "vue";
import { useStore } from "vuex";
import defaultAvatar from "@/assets/images/visitor.png";

export default {
    name: "ChatRoomShow",
    props: {
        messagelist: {
            type: Array,
            required: true,
        }
    },
    setup(props) {
        const listRef = ref(null);
        const store = useStore();

        const scrollToBottom = () => {
            nextTick(() => {
                if (listRef.value) {
                    listRef.value.scrollTo({
                        top: listRef.value.scrollHeight,
                        behavior: "smooth",
                    });
                }
            });
        };

        watch(
            () => props.messagelist.length,
            () => {
                scrollToBottom();
            },
            { immediate: true }
        );

        const isOutgoing = (msg) => {
            return msg.error_message === "send_message" || msg.username === store.state.user.username;
        };

        const avatarSrc = (msg) => {
            if (isOutgoing(msg)) {
                return store.state.user.photo || defaultAvatar;
            }
            const candidates = [
                msg.photo,
                msg.avatar,
                msg.avatar_url,
                msg.avatarUrl,
                msg.user_photo,
                msg.profile_photo,
                msg.user?.photo,
                msg.user?.avatar,
                msg.sender?.photo,
                msg.sender?.avatar,
            ];
            for (const url of candidates) {
                if (url) return url;
            }
            return defaultAvatar;
        };

        const formatTime = (val) => {
            if (!val) return "刚刚";
            const date = typeof val === "number" ? new Date(val) : new Date(val);
            if (isNaN(date.getTime())) return "刚刚";
            const pad = (n) => (n < 10 ? "0" + n : n);
            return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
        };

        return {
            listRef,
            avatarSrc,
            formatTime,
            isOutgoing,
        };
    },
}
</script>

<style scoped>
.chat-panel {
    min-height: 0;
    background: transparent;
}

.chat-body {
    height: min(58vh, 560px);
    min-height: 420px;
    overflow-y: auto;
    padding: 22px 24px;
    scroll-behavior: smooth;
}

.empty-state {
    display: grid;
    place-items: center;
    min-height: 100%;
    padding: 32px;
    text-align: center;
    color: var(--app-muted);
}

.empty-state img {
    width: 70px;
    height: 70px;
    margin-bottom: 16px;
    border-radius: 22px;
    box-shadow: var(--app-shadow-soft);
}

.empty-state h2 {
    margin: 0 0 8px;
    color: var(--app-text);
    font-size: 1.35rem;
    font-weight: 900;
}

.empty-state p {
    margin: 0;
}

.message-row {
    display: flex;
    align-items: flex-end;
    gap: 12px;
    margin-bottom: 18px;
}

.message-row.outgoing {
    flex-direction: row-reverse;
}

.avatar {
    width: 40px;
    height: 40px;
    flex: 0 0 40px;
    object-fit: cover;
    background: var(--app-surface-muted);
    border: 2px solid rgba(20, 184, 166, 0.32);
    border-radius: 50%;
    box-shadow: var(--app-shadow-soft);
}

.bubble-wrap {
    max-width: min(72%, 680px);
}

.message-row.outgoing .bubble-wrap {
    text-align: right;
}

.message-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 5px 2px;
}

.message-row.outgoing .message-meta {
    justify-content: flex-end;
    margin-right: 2px;
}

.username {
    color: var(--app-text);
    font-size: 0.82rem;
    font-weight: 800;
}

.time {
    color: var(--app-muted);
    font-size: 0.75rem;
    font-weight: 700;
}

.message-bubble {
    display: inline-block;
    max-width: 100%;
    padding: 12px 15px;
    color: var(--app-text);
    line-height: 1.7;
    text-align: left;
    white-space: pre-wrap;
    word-break: break-word;
    background: var(--app-surface-strong);
    border: 1px solid var(--app-border);
    border-radius: 16px 16px 16px 6px;
    box-shadow: var(--app-shadow-soft);
}

.message-row.outgoing .message-bubble {
    color: #fff;
    background: linear-gradient(135deg, var(--app-primary), #2563eb);
    border-color: transparent;
    border-radius: 16px 16px 6px 16px;
}

@media (max-width: 768px) {
    .chat-body {
        height: 56vh;
        min-height: 360px;
        padding: 18px 14px;
    }

    .bubble-wrap {
        max-width: 82%;
    }
}
</style>
